import {
	Stack,
	Box,
	Button,
	Inline,
	Icons,
	Divider,
	Text,
	Tooltip,
	noSpinnersClassName,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import {
	Form,
	FieldArray,
	arrayMutators,
	validateFormValues,
	withServerErrors,
	SubmissionError,
	TextInputField,
	PercentageInputField,
	MarkdownInputField,
	getFieldArrayItemKey,
	addKeyToFieldArrayItem,
	useForm,
} from "@sembark-travel/ui-form"
import { useXHR } from "@sembark-travel/xhr"
import { type CSSProperties, useEffect, useMemo, useRef, useState } from "react"
import * as Validator from "yup"
import {
	type TCancellationPolicyDetail,
	cancellationPoliciesXHR,
} from "./store"
import { formValuesToPayload, getSegmentColor, NO_PENALTY_COLOR } from "./utils"
import { isBefore, isSameOrAfter } from "@sembark-travel/datetime-utils"
import { AddDateRangeRuleButton, DateRangeDialog } from "../shared/Policy"
import {
	dateRangeSchema,
	formatDateRange,
	type TDateRange,
} from "../shared/policies"

export type TCancellationConditionFormValues = {
	min_days_before_checkin?: number
	max_days_before_checkin?: number
	penalty_value?: number
}

export type TCancellationRuleFormValues = {
	valid_from: Date | null
	valid_to: Date | null
	conditions: TCancellationConditionFormValues[]
}

export interface INewCancellationPolicyFormSchema {
	name: string
	base_rule: TCancellationRuleFormValues
	rules: TCancellationRuleFormValues[]
	additional_info: string
}

// A blank interval. `minDays` is the day the interval starts on, which is
// always decided by the previous interval, never typed in.
function makeEmptyCondition(
	minDays?: number
): TCancellationConditionFormValues {
	return {
		min_days_before_checkin: minDays,
		max_days_before_checkin: undefined,
		penalty_value: undefined,
	}
}

// Intervals are contiguous, so an interval always starts the day after the
// previous one ends. Returns undefined when the previous end is not usable.
function getNextMinDays(maxDays?: number): number | undefined {
	if (maxDays === undefined || maxDays === null || String(maxDays) === "")
		return undefined
	const max = Number(maxDays)
	if (isNaN(max)) return undefined
	return max + 1
}

const conditionSchema = Validator.object().shape({
	min_days_before_checkin: Validator.number()
		.integer("Must be a whole number")
		.min(0, "Must be ≥ 0")
		.max(999, "Must be ≤ 999")
		.required("Required"),
	max_days_before_checkin: Validator.number()
		.integer("Must be a whole number")
		.min(0, "Must be ≥ 0")
		.max(999, "Must be ≤ 999")
		.test("more-or-equal-to-from", "Must be ≥ From", function (value) {
			const min = this.parent.min_days_before_checkin
			if (min == null || value == null) return true
			return value >= min
		})
		.required("Required"),
	// The payload only ever carries a percentage, so the bound is unconditional
	penalty_value: Validator.number()
		.min(0, "Cannot be negative")
		.max(100, "Must be ≤ 100%")
		.required("Required"),
})

const baseRuleConditionsSchema = Validator.array()
	.of(conditionSchema)
	.min(1, "Add at least one rule")
	.test(
		"no-overlapping-ranges",
		"Ranges must not overlap or have gaps",
		function (conditions) {
			if (!conditions || conditions.length < 2) return true

			const filled = conditions
				.map((c, index) => ({
					index,
					min: Number(c.min_days_before_checkin),
					max: Number(c.max_days_before_checkin),
				}))
				.filter((c) => !isNaN(c.min) && !isNaN(c.max))
				.sort((a, b) => a.min - b.min)

			// e.g. 0-2, 3-6, 7-10 => every next interval starts at prev max + 1
			for (let i = 0; i < filled.length - 1; i++) {
				const cur = filled[i]
				const next = filled[i + 1]

				if (next.min <= cur.max)
					return this.createError({
						path: `${this.path}[${next.index}].min_days_before_checkin`,
						message: `Overlaps with row ${cur.index + 1}`,
					})

				if (next.min > cur.max + 1)
					return this.createError({
						path: `${this.path}[${next.index}].min_days_before_checkin`,
						message:
							next.min - 1 === cur.max + 1
								? `Gap: Day ${cur.max + 1}`
								: `Gap: Days ${cur.max + 1}–${next.min - 1}`,
					})
			}

			return true
		}
	)

const dateRangeRuleSchema = dateRangeSchema.shape({
	conditions: baseRuleConditionsSchema,
})

const validationSchema = Validator.object().shape({
	name: Validator.string()
		.min(4, "Policy name cannot be shorter than 4 characters")
		.max(90, "Policy name cannot be longer than 90 characters")
		.required("Policy name is required"),
	additional_info: Validator.string()
		.optional()
		.max(10000, "Keep the text under 10000 characters"),
	base_rule: Validator.object().shape({
		conditions: baseRuleConditionsSchema,
	}),
	rules: Validator.array()
		.of(dateRangeRuleSchema)
		.test(
			"no-overlapping-date-ranges",
			"Date ranges must not overlap",
			function (rules) {
				if (!rules || rules.length < 2) return true
				const filled = rules
					.map((r, index) => ({
						index,
						from: r?.valid_from,
						to: r?.valid_to,
					}))
					.filter(
						(r): r is { index: number; from: Date; to: Date } =>
							r.from !== null && r.to !== null
					)
					.sort((a, b) =>
						isBefore(a.from, b.from) ? -1 : isBefore(b.from, a.from) ? 1 : 0
					)

				for (let i = 0; i < filled.length - 1; i++) {
					const current = filled[i]
					const next = filled[i + 1]

					// Check overlap. Dates are picked in a dialog which already
					// prevents overlaps, so this only guards the saved policies
					if (isSameOrAfter(current.to, next.from, "day")) {
						return this.createError({
							message: `${formatDateRange(next.from, next.to)} overlaps with ${formatDateRange(current.from, current.to)}`,
						})
					}
				}
				return true
			}
		),
})

export function NewCancellationPolicyForm({
	onSubmit,
	onCancel,
	initialValues: propInitialValues,
	isEdit,
	isDefault = false,
	isNameHidden = false,
	submitLabel,
}: {
	onSubmit: (values: INewCancellationPolicyFormSchema) => Promise<unknown>
	onCancel?: () => void
	initialValues?: INewCancellationPolicyFormSchema
	isEdit?: boolean
	isDefault?: boolean
	isNameHidden?: boolean
	submitLabel?: React.ReactNode
}) {
	const initialValues = useMemo<INewCancellationPolicyFormSchema>(
		() =>
			propInitialValues || {
				name: "",
				additional_info: "",
				base_rule: {
					valid_from: null,
					valid_to: null,
					conditions: [addKeyToFieldArrayItem(makeEmptyCondition(0))],
				},
				rules: [],
			},
		[propInitialValues]
	)

	return (
		<Form<INewCancellationPolicyFormSchema>
			initialValues={initialValues}
			validate={validateFormValues(
				isNameHidden ? validationSchema.omit(["name"]) : validationSchema
			)}
			onSubmit={withServerErrors(onSubmit)}
			subscription={{ submitting: true }}
			mutators={{ ...arrayMutators }}
		>
			{({ submitting, handleSubmit, form }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						{!isNameHidden ? (
							<TextInputField
								label="Policy name"
								name="name"
								type="text"
								placeholder="e.g. Hotel-default Policy"
								maxWidth="xs"
								disabled={isDefault}
							/>
						) : null}
						<RuleBlock name="base_rule" isBase />
						<FieldArray<TCancellationRuleFormValues>
							name="rules"
							subscription={{ value: true }}
						>
							{({ fields }) => {
								const rules: Array<TCancellationRuleFormValues> =
									fields.value || []
								return (
									<Stack gap="4">
										{fields.map((ruleName, ruleIndex) => (
											<RuleBlock
												key={getFieldArrayItemKey(form as never, ruleName)}
												name={ruleName}
												isBase={false}
												validFrom={rules[ruleIndex]?.valid_from}
												validTo={rules[ruleIndex]?.valid_to}
												otherDateRanges={rules.filter(
													(_, index) => index !== ruleIndex
												)}
												onChangeDates={(dates) =>
													fields.update(ruleIndex, {
														...rules[ruleIndex],
														...dates,
													})
												}
												onRemoveRule={() => fields.remove(ruleIndex)}
											/>
										))}
										<AddDateRangeRuleButton
											existingDateRanges={rules}
											onAdd={(dates) =>
												fields.push(
													addKeyToFieldArrayItem({
														...dates,
														conditions: [
															addKeyToFieldArrayItem(makeEmptyCondition(0)),
														],
													})
												)
											}
										/>
									</Stack>
								)
							}}
						</FieldArray>
						<Divider marginY="2" />
						<MarkdownInputField
							label="Additional information"
							name="additional_info"
							placeholder="Any special remarks."
							secondaryLabel="optional"
							rows={4}
						/>
						<Divider sm />
						<SubmissionError />
						<Inline gap="4">
							<Button
								type="submit"
								disabled={submitting}
								status="primary"
								level="primary"
								style={{ opacity: submitting ? 0.5 : 1 }}
							>
								{submitting ? (
									<Box>
										<Icons.Spinner />
									</Box>
								) : null}
								{submitting
									? isEdit
										? "Updating..."
										: "Saving..."
									: submitLabel || (isEdit ? "Update" : "Save")}
							</Button>
							{onCancel ? (
								<Button
									onClick={onCancel}
									level="tertiary"
									disabled={submitting}
									style={{ opacity: submitting ? 0.5 : 1 }}
								>
									Cancel
								</Button>
							) : null}
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

function RuleBlock({
	name,
	isBase,
	validFrom,
	validTo,
	otherDateRanges,
	onChangeDates,
	onRemoveRule,
}: {
	name: string
	isBase: boolean
	validFrom?: Date | null
	validTo?: Date | null
	// ranges of the other rules, which this rule must not overlap with
	otherDateRanges?: Array<TDateRange>
	onChangeDates?: (dates: TDateRange) => void
	onRemoveRule?: () => void
}) {
	const conditionsName = `${name}.conditions`
	const form = useForm()
	const [isEditingDates, editDates, stopEditingDates] = useDialog()
	const [isRemovingRule, askToRemoveRule, cancelRemoveRule] = useDialog()

	return (
		<Stack gap="4" borderWidth="1" rounded="md">
			{!isBase ? (
				<Inline
					padding="3"
					gap="2"
					alignItems="center"
					justifyContent="between"
					flexWrap="wrap"
					bgColor="primary"
					borderBottomWidth="1"
				>
					<Inline gap="2" alignItems="center" flexWrap="wrap">
						<Text fontWeight="semibold" fontSize="md">
							{formatDateRange(validFrom, validTo)} (Check-in)
						</Text>
						{onChangeDates ? (
							<Button
								inline
								onClick={editDates}
								title="Edit the check-in dates"
							>
								<Icons.Pencil />
							</Button>
						) : null}
					</Inline>
					{onRemoveRule ? (
						<Button onClick={askToRemoveRule} size="sm" level="tertiary">
							<Icons.Cancel /> Remove
						</Button>
					) : null}
				</Inline>
			) : (
				<Inline
					padding="3"
					gap="2"
					alignItems="center"
					bgColor="success"
					borderBottomWidth="1"
				>
					<Text fontSize="md" fontWeight="semibold">
						Base Policy
					</Text>
					<Tooltip content="Applies when no date range matches the check-in date">
						<Box>
							<Icons.Info />
						</Box>
					</Tooltip>
				</Inline>
			)}

			<Stack gap="1" padding="4">
				<FieldArray<TCancellationConditionFormValues>
					name={conditionsName}
					subscription={{ value: true }}
				>
					{({ fields }) => {
						const conditions: Array<TCancellationConditionFormValues> =
							fields.value || []

						// intervals are contiguous: each one starts where the previous ended
						function setMinDays(
							index: number,
							condition: TCancellationConditionFormValues | undefined,
							minDays?: number
						) {
							if (!condition) return
							fields.update(index, {
								...condition,
								min_days_before_checkin: minDays,
							})
						}

						return (
							<Stack gap="4">
								<Box
									style={{
										maxWidth: "fit-content",
									}}
									rounded="md"
									borderWidth="1"
									overflow="auto"
									borderBottomWidth="0"
								>
									<Box as="table">
										<Box as="thead">
											<Box as="tr" bgColor="subtle">
												{[
													"From (days)",
													"To (days)",
													<Inline gap="2" alignItems="center">
														Penalty %
														<Tooltip content="% of Total Booking Amount">
															<Box>
																<Icons.Info />
															</Box>
														</Tooltip>
													</Inline>,
													"",
													...(Number(fields.length) > 1 ? [""] : []),
												].map((h, i) => (
													<Box
														as="th"
														key={i}
														padding="3"
														textAlign="left"
														fontSize="sm"
														fontWeight="semibold"
														color="muted"
														textTransform="uppercase"
														borderBottomWidth="1"
													>
														{h}
													</Box>
												))}
											</Box>
										</Box>
										<Box as="tbody">
											{fields.map((condName, condIndex) => (
												<ConditionTableRow
													key={getFieldArrayItemKey(form, condName)}
													name={condName}
													condition={conditions[condIndex] || {}}
													showRemove={Number(fields.length) > 1}
													onMaxDaysChange={(maxDays) =>
														setMinDays(
															condIndex + 1,
															conditions[condIndex + 1],
															getNextMinDays(maxDays)
														)
													}
													onRemove={() => {
														const previousCondition = conditions[condIndex - 1]
														const nextCondition = conditions[condIndex + 1]
														fields.remove(condIndex)
														// the next interval takes this row's place, so re-chain it
														setMinDays(
															condIndex,
															nextCondition,
															condIndex === 0
																? 0
																: getNextMinDays(
																		previousCondition?.max_days_before_checkin
																	)
														)
													}}
												/>
											))}
										</Box>
									</Box>
								</Box>
								<Box>
									<Button
										size="sm"
										level="secondary"
										onClick={() => {
											const lastCondition = conditions[conditions.length - 1]
											fields.push(
												addKeyToFieldArrayItem(
													makeEmptyCondition(
														lastCondition
															? getNextMinDays(
																	lastCondition.max_days_before_checkin
																)
															: 0
													)
												)
											)
										}}
									>
										<Icons.Plus /> Add More Rule
									</Button>
								</Box>
								<PenaltyTimelineDisplay conditions={conditions} />
							</Stack>
						)
					}}
				</FieldArray>
			</Stack>
			{onChangeDates ? (
				<DateRangeDialog
					open={isEditingDates}
					title="Edit Date"
					submitLabel="Update"
					existingDateRanges={otherDateRanges}
					initialValues={{
						valid_from: validFrom || null,
						valid_to: validTo || null,
					}}
					onSuccess={(dates) => {
						onChangeDates(dates)
						stopEditingDates()
					}}
					onClose={stopEditingDates}
				/>
			) : null}
			{onRemoveRule ? (
				<Dialog
					open={isRemovingRule}
					onClose={cancelRemoveRule}
					title="Remove Rule"
					sm
				>
					<Dialog.Body>
						<Text>
							Are you sure you want to Remove this Date Range Rules from this
							Policy?
						</Text>
					</Dialog.Body>
					<Dialog.Footer>
						<Inline justifyContent="end">
							<Button
								level="primary"
								status="danger"
								onClick={() => {
									cancelRemoveRule()
									onRemoveRule()
								}}
							>
								Yes, Remove
							</Button>
						</Inline>
					</Dialog.Footer>
				</Dialog>
			) : null}
		</Stack>
	)
}

function ConditionTableRow({
	name,
	condition,
	onMaxDaysChange,
	onRemove,
	showRemove,
}: {
	name: string
	condition: TCancellationConditionFormValues
	// called when this interval's end changes, so the next one can follow it
	onMaxDaysChange: (maxDays?: number) => void
	onRemove: () => void
	showRemove: boolean
}) {
	const maxDays = condition.max_days_before_checkin
	const syncedMaxDays = useRef(maxDays)

	useEffect(() => {
		// only follow the changes, so existing policies are left as they are
		if (syncedMaxDays.current === maxDays) return
		syncedMaxDays.current = maxDays
		onMaxDaysChange(maxDays)
	}, [maxDays, onMaxDaysChange])

	const dotColor = getSegmentColor(condition)
	return (
		<Box as="tr" borderBottomWidth="1">
			<Box as="td" paddingX="3" paddingY="2">
				<TextInputField
					name={`${name}.min_days_before_checkin`}
					type="number"
					min={0}
					style={{ maxWidth: "6rem" }}
					disabled
					className={noSpinnersClassName}
					borderWidth="0"
					color="muted"
				/>
			</Box>
			<Box as="td" paddingX="3" paddingY="2">
				<TextInputField
					name={`${name}.max_days_before_checkin`}
					type="number"
					min={0}
					style={{ maxWidth: "6rem" }}
					className={noSpinnersClassName}
				/>
			</Box>
			<Box as="td" paddingX="3" paddingY="2" paddingRight="0">
				<Inline alignItems="center" gap="6">
					<PercentageInputField
						name={`${name}.penalty_value`}
						className={noSpinnersClassName}
					/>
					<Box size="3" style={{ backgroundColor: dotColor }} rounded="full" />
				</Inline>
			</Box>
			{showRemove ? (
				<Box as="td" paddingX="3" paddingY="2">
					<Button
						onClick={onRemove}
						size="sm"
						level="secondary"
						title="Remove this rule"
					>
						<Icons.Cancel />
					</Button>
				</Box>
			) : null}
		</Box>
	)
}

// ~100px, so the open ended "Free" interval always stays readable
const FREE_SEGMENT_MIN_WIDTH = "6.25rem"

// The vertical (below sm) variant of the same bar. Heights play the role the
// widths play above, so the "Free" cap and the intervals get their own minimums.
const FREE_SEGMENT_MIN_HEIGHT = "3rem"
const SEGMENT_MIN_HEIGHT = "2.5rem"
// the height the intervals share out between them. It is a minimum, so a policy
// with many intervals grows the bar instead of overflowing it.
const TIMELINE_MIN_HEIGHT = "20rem"
// the day range labels sit in a fixed column, so they all end at the same edge
const LABEL_COLUMN_WIDTH = "5rem"
const BAR_WIDTH = "2rem"

function SegmentTooltipContent({
	color,
	children,
}: {
	color: string
	children: React.ReactNode
}) {
	return (
		<Inline gap="2" alignItems="center">
			<Box
				size="3"
				rounded="full"
				flexShrink="0"
				style={{ backgroundColor: color }}
			/>
			<Box>{children}</Box>
		</Inline>
	)
}

// number of days an interval covers, both ends inclusive
function getIntervalDays(condition: {
	min_days_before_checkin?: number
	max_days_before_checkin?: number
}): number {
	const min = Number(condition.min_days_before_checkin || 0)
	const max = Number(condition.max_days_before_checkin || 0)
	if (isNaN(min) || isNaN(max)) return 1
	return Math.abs(max - min) + 1
}

function formatCheckinLabel(min?: number, max?: number): string {
	if (
		min === null ||
		max === null ||
		min === undefined ||
		max === undefined ||
		isNaN(min) ||
		isNaN(max)
	)
		return "–"
	if (min > max) [min, max] = [max, min]

	if (min === max) {
		return min === 0 ? "check-in" : `Day ${min}`
	}
	if (max === 1) {
		return "check-in – 24 hr"
	}
	return `${min}–${max} days`
}

// The open ended interval before the first one, which carries no penalty
function formatFreeLabel(maxDays: number): string {
	return `> ${maxDays === 1 ? "24 hrs" : `${maxDays} days`}`
}

export function PenaltyTimelineDisplay({
	conditions,
}: {
	conditions: Array<TCancellationConditionFormValues>
}) {
	const { filtered, maxDays } = useMemo(() => {
		const filtered = (conditions || [])
			.filter(
				(c): c is TCancellationConditionFormValues =>
					c.min_days_before_checkin !== undefined &&
					c.max_days_before_checkin !== undefined
			)
			.sort(
				(a, b) =>
					Number(b.min_days_before_checkin) - Number(a.min_days_before_checkin)
			)

		return {
			filtered,
			maxDays: Math.max(
				0,
				...filtered.map((c) => Number(c.max_days_before_checkin || 0))
			),
		}
	}, [conditions])

	if (filtered.length === 0) return null

	// The bar reads left to right on a wide screen and top to bottom below the
	// `sm` breakpoint. Both variants are rendered and swapped with css, because
	// the segment sizing and the rounded cap are direction specific and neither
	// can be expressed as a responsive style prop.
	return (
		<>
			<Divider marginY="4" />
			<Box display={{ xs: "none", sm: "block" }}>
				<HorizontalPenaltyTimeline filtered={filtered} maxDays={maxDays} />
			</Box>
			<Box display={{ xs: "block", sm: "none" }}>
				<VerticalPenaltyTimeline filtered={filtered} maxDays={maxDays} />
			</Box>
		</>
	)
}

function HorizontalPenaltyTimeline({
	filtered,
	maxDays,
}: {
	filtered: Array<TCancellationConditionFormValues>
	maxDays: number
}) {
	// Widths are proportional to the number of days each interval covers. The
	// "Free" interval is open ended (> maxDays), so it gets a fixed minimum
	// width when there are other intervals to share the space with.
	const isSingleInterval = filtered.length === 1
	const freeSegStyle: CSSProperties = isSingleInterval
		? { flex: "1 1 50%" }
		: { flex: `0 0 ${FREE_SEGMENT_MIN_WIDTH}` }
	const segStyles: Array<CSSProperties> = isSingleInterval
		? [{ flex: "1 1 50%" }]
		: filtered.map((c) => ({
				flex: `${getIntervalDays(c)} 1 0%`,
				minWidth: "6rem",
			}))

	return (
		<Stack gap="0">
			<Inline gap="4" alignItems="center">
				<Text fontSize="sm" fontWeight="semibold" whiteSpace="nowrap">
					← Earlier
				</Text>
				<Inline flex="1" overflow="hidden" height="5">
					<Tooltip
						content={
							<SegmentTooltipContent color={NO_PENALTY_COLOR}>
								No Cancellation charges
							</SegmentTooltipContent>
						}
					>
						<Box
							style={{
								...freeSegStyle,
								backgroundColor: NO_PENALTY_COLOR,
							}}
							height="full"
							display="flex"
							alignItems="center"
							justifyContent="center"
							roundedLeft="full"
						>
							<Text fontSize="xs" fontWeight="semibold">
								Free
							</Text>
						</Box>
					</Tooltip>
					{filtered.map((c, i) => (
						<Tooltip
							key={i}
							content={
								<SegmentTooltipContent color={getSegmentColor(c)}>
									{`Cancellation charge is ${c.penalty_value || 0}% of total booking amount`}
								</SegmentTooltipContent>
							}
						>
							<Box
								style={{
									...segStyles[i],
									backgroundColor: getSegmentColor(c),
								}}
								borderLeftColor="white"
								borderLeftWidth="2"
								height="full"
								display="flex"
								alignItems="center"
								justifyContent="center"
							>
								<Text
									fontSize="xs"
									fontWeight="semibold"
									color={
										Number(c.penalty_value || 0) <= 50 ? "default" : "white"
									}
								>
									{c.penalty_value || 0}%
								</Text>
							</Box>
						</Tooltip>
					))}
				</Inline>
				<Stack>
					<Text fontSize="xl">
						<span role="img" aria-label="check-in">
							🏁
						</span>
					</Text>
					<Text fontSize="sm" fontWeight="semibold" whiteSpace="nowrap">
						Check-in
					</Text>
				</Stack>
			</Inline>
			<Inline gap="4">
				<Text
					fontSize="sm"
					fontWeight="normal"
					whiteSpace="nowrap"
					style={{ visibility: "hidden" }}
				>
					← Earlier
				</Text>

				<Inline flex="1">
					<Box style={freeSegStyle} textAlign="center" fontSize="sm">
						{formatFreeLabel(maxDays)}
					</Box>
					{filtered.map((c, i) => (
						<Box key={i} style={segStyles[i]} textAlign="center" fontSize="sm">
							{formatCheckinLabel(
								c.min_days_before_checkin,
								c.max_days_before_checkin
							)}
						</Box>
					))}
				</Inline>
				<Text
					fontSize="sm"
					fontWeight="semibold"
					whiteSpace="nowrap"
					style={{ visibility: "hidden" }}
				>
					Check-in
				</Text>
			</Inline>
		</Stack>
	)
}

function VerticalPenaltyTimeline({
	filtered,
	maxDays,
}: {
	filtered: Array<TCancellationConditionFormValues>
	maxDays: number
}) {
	// The vertical twin of the widths above: each row grows with the number of
	// days its interval covers, and the labels sit in a column beside the bar.
	const isSingleInterval = filtered.length === 1
	const freeRowStyle: CSSProperties = isSingleInterval
		? { flex: "1 1 50%" }
		: { flex: `0 0 ${FREE_SEGMENT_MIN_HEIGHT}` }
	const rowStyles: Array<CSSProperties> = isSingleInterval
		? [{ flex: "1 1 50%" }]
		: filtered.map((c) => ({
				flex: `${getIntervalDays(c)} 1 0%`,
				minHeight: SEGMENT_MIN_HEIGHT,
			}))

	return (
		<Stack gap="2" alignItems="center">
			<TimelineCaption>
				<Text fontSize="sm" fontWeight="semibold" whiteSpace="nowrap">
					↑ Earlier
				</Text>
			</TimelineCaption>
			<Stack gap="0" style={{ minHeight: TIMELINE_MIN_HEIGHT }}>
				<Inline gap="3" style={freeRowStyle}>
					<TimelineRowLabel>{formatFreeLabel(maxDays)}</TimelineRowLabel>
					<Tooltip
						content={
							<SegmentTooltipContent color={NO_PENALTY_COLOR}>
								No Cancellation charges
							</SegmentTooltipContent>
						}
					>
						<Box
							style={{
								width: BAR_WIDTH,
								backgroundColor: NO_PENALTY_COLOR,
							}}
							display="flex"
							alignItems="center"
							justifyContent="center"
							roundedTop="full"
						>
							<Text fontSize="xs" fontWeight="semibold">
								Free
							</Text>
						</Box>
					</Tooltip>
				</Inline>
				{filtered.map((c, i) => (
					<Inline key={i} gap="3" style={rowStyles[i]}>
						<TimelineRowLabel>
							{formatCheckinLabel(
								c.min_days_before_checkin,
								c.max_days_before_checkin
							)}
						</TimelineRowLabel>
						<Tooltip
							content={
								<SegmentTooltipContent color={getSegmentColor(c)}>
									{`Cancellation charge is ${c.penalty_value || 0}% of total booking amount`}
								</SegmentTooltipContent>
							}
						>
							<Box
								style={{
									width: BAR_WIDTH,
									backgroundColor: getSegmentColor(c),
								}}
								borderTopColor="white"
								borderTopWidth="2"
								display="flex"
								alignItems="center"
								justifyContent="center"
							>
								<Text
									fontSize="xs"
									fontWeight="semibold"
									color={
										Number(c.penalty_value || 0) <= 50 ? "default" : "white"
									}
								>
									{c.penalty_value || 0}%
								</Text>
							</Box>
						</Tooltip>
					</Inline>
				))}
			</Stack>
			<TimelineCaption>
				<Stack alignItems="center">
					<Text fontSize="xl">
						<span role="img" aria-label="check-in">
							🏁
						</span>
					</Text>
					<Text fontSize="sm" fontWeight="semibold" whiteSpace="nowrap">
						Check-in
					</Text>
				</Stack>
			</TimelineCaption>
		</Stack>
	)
}

// Captions are offset by the label column, so they read as centered on the bar
// itself instead of on the label + bar pair.
function TimelineCaption({ children }: { children: React.ReactNode }) {
	return (
		<Inline gap="3">
			<Box style={{ width: LABEL_COLUMN_WIDTH }} />
			<Box
				style={{ width: BAR_WIDTH }}
				display="flex"
				justifyContent="center"
				textAlign="center"
			>
				{children}
			</Box>
		</Inline>
	)
}

function TimelineRowLabel({ children }: { children: React.ReactNode }) {
	return (
		<Box
			style={{ width: LABEL_COLUMN_WIDTH }}
			display="flex"
			alignItems="center"
			justifyContent="end"
		>
			<Text fontSize="sm" textAlign="right">
				{children}
			</Text>
		</Box>
	)
}

function NewCancellationPolicy({
	onSuccess,
	onCancel,
}: {
	onSuccess: (policy: TCancellationPolicyDetail) => void
	onCancel?: () => void
}) {
	const xhr = useXHR()
	return (
		<NewCancellationPolicyForm
			onCancel={onCancel}
			onSubmit={async (values) => {
				const detail = await cancellationPoliciesXHR(xhr).create(
					formValuesToPayload(values)
				)
				onSuccess(detail)
			}}
		/>
	)
}

export function NewCancellationPolicyInDialog({
	onSuccess,
	children,
}: {
	children: (props: { onClick: () => void }) => React.ReactNode
	onSuccess: (policy: TCancellationPolicyDetail) => void
}) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({ onClick: open })}
			<Dialog open={isOpen} onClose={close} lg>
				<Dialog.Header>
					<Dialog.Title>Create Cancellation Policy</Dialog.Title>
				</Dialog.Header>
				<Dialog.Body>
					<NewCancellationPolicy
						onSuccess={(policy) => {
							close()
							onSuccess(policy)
						}}
						onCancel={close}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

// Onboarding starts the tenant off with the full amount charged within a week
// of check-in, which they can adjust before moving on.
function makeDefaultCancellationPolicyFormValues(): INewCancellationPolicyFormSchema {
	return {
		name: "",
		additional_info: "",
		base_rule: {
			valid_from: null,
			valid_to: null,
			conditions: [
				addKeyToFieldArrayItem({
					min_days_before_checkin: 0,
					max_days_before_checkin: 7,
					penalty_value: 100,
				}),
			],
		},
		rules: [],
	}
}

export function NewDefaultCancellationPolicy({
	onSuccess,
}: {
	onSuccess: () => void
}) {
	const xhr = useXHR()
	const initialValues = useMemo(
		() => makeDefaultCancellationPolicyFormValues(),
		[]
	)
	return (
		<NewCancellationPolicyForm
			initialValues={initialValues}
			isNameHidden
			submitLabel={
				<>
					<Icons.Ok /> Next
				</>
			}
			onSubmit={async (values) => {
				// the backend names the policy and marks the first one as default
				const { name: _name, ...payload } = formValuesToPayload(values)
				await cancellationPoliciesXHR(xhr).create(payload)
				onSuccess()
			}}
		/>
	)
}

function EditCancellationPolicy({
	detailId,
	onSuccess,
	onCancel,
	data,
	isDefault = false,
}: {
	detailId: number
	onSuccess: (policy: TCancellationPolicyDetail) => void
	onCancel?: () => void
	data: INewCancellationPolicyFormSchema
	isDefault: boolean
}) {
	const xhr = useXHR()
	return (
		<NewCancellationPolicyForm
			initialValues={data}
			onCancel={onCancel}
			onSubmit={async (values) => {
				const updated = await cancellationPoliciesXHR(xhr).update(
					detailId,
					formValuesToPayload(values)
				)
				if ("message" in updated) {
					showSnackbar(updated.message)
					return
				}
				onSuccess(updated)
			}}
			isDefault={isDefault}
			isEdit
		/>
	)
}

export function EditCancellationPolicyInDialog({
	onSuccess,
	children,
	detailId,
	isDefault = false,
}: {
	children: (props: {
		onClick: (data: INewCancellationPolicyFormSchema) => void
	}) => React.ReactNode
	onSuccess: (policy: TCancellationPolicyDetail) => void
	detailId: number
	isDefault: boolean
}) {
	const [isOpen, open, close] = useDialog()
	const [values, setValues] = useState<INewCancellationPolicyFormSchema>()
	return (
		<>
			{children({
				onClick: (data) => {
					open()
					setValues(data)
				},
			})}
			<Dialog open={isOpen} onClose={close} lg>
				<Dialog.Header>
					<Dialog.Title>Edit Cancellation Policy</Dialog.Title>
				</Dialog.Header>
				<Dialog.Body>
					{values ? (
						<EditCancellationPolicy
							detailId={detailId}
							onSuccess={(policy) => {
								onSuccess(policy)
								close()
							}}
							onCancel={close}
							data={values}
							isDefault={isDefault}
						/>
					) : null}
				</Dialog.Body>
			</Dialog>
		</>
	)
}
